import type { ItemDetails } from "../types/TestTarget";

export type LooseItemParams = {
  text?: string;
  /** @deprecated 旧 HTML 要素名。新規・保存では itemCategory / itemSubCategory のみ使用 */
  type?: string;
  itemCategory?: string;
  itemSubCategory?: string;
  /** @deprecated 旧キー。読取互換のみ */
  inputType?: string;
  status?: string;
  options?: { checkbox?: Record<string, unknown> } & Record<string, unknown>;
  selector?: string | Record<string, unknown>;
  is_manual?: boolean;
  fail_count?: number;
  is_download?: boolean;
  customTypeFlg?: boolean;
  force_analyze?: boolean;
  htmlAttributes?: Record<string, unknown>;
  transitions?: Array<Record<string, unknown>>;
  [key: string]: unknown;
};

const baseItemParams: LooseItemParams = {
  text: "",
  itemCategory: "Action",
  itemSubCategory: "button",
  status: "draft",
  options: { checkbox: {} },
  selector: {},
  is_manual: false,
  fail_count: 0,
  is_download: false,
  customTypeFlg: false,
  force_analyze: false,
  htmlAttributes: { required: false },
  transitions: [],
};

export const buildDefaultItemParams = (): LooseItemParams =>
  JSON.parse(JSON.stringify(baseItemParams));

export const normalizeItemParams = (
  params?: LooseItemParams | null
): LooseItemParams => {
  if (!params || typeof params !== "object") {
    return buildDefaultItemParams();
  }

  const merged: LooseItemParams = { ...buildDefaultItemParams(), ...params };

  merged.options = {
    ...buildDefaultItemParams().options,
    ...(params.options || {}),
  };

  merged.htmlAttributes = {
    ...buildDefaultItemParams().htmlAttributes,
    ...(params.htmlAttributes || {}),
  };

  if (Array.isArray(params.transitions)) {
    merged.transitions = params.transitions;
  } else if (params.transitions) {
    merged.transitions = [params.transitions as Record<string, unknown>];
  } else {
    merged.transitions = [];
  }

  return merged;
};

/**
 * Item の params を ItemDetails として読む。
 * selector の有無は問わない（CSV インポート等のセレクタ無し Item でも
 * transitions 等を読めるようにする）。
 */
export function as_item_details(params: unknown): ItemDetails | undefined {
  if (!params || typeof params !== "object" || Array.isArray(params)) {
    return undefined;
  }
  return params as ItemDetails;
}

/**
 * params.transitions が 1 件以上あるか（単一オブジェクト形式も後方互換で true）。
 */
export function has_item_transitions(params: unknown): boolean {
  const details = as_item_details(params);
  if (!details?.transitions) return false;
  if (Array.isArray(details.transitions)) {
    return details.transitions.length > 0;
  }
  // 単一オブジェクト形式（後方互換）
  return true;
}

/**
 * params.transitions を配列に正規化する。未設定・不正時は空配列。
 */
export function normalize_item_transitions(
  params: unknown
): NonNullable<ItemDetails["transitions"]> {
  const details = as_item_details(params);
  if (!details?.transitions) return [];
  if (Array.isArray(details.transitions)) return details.transitions;
  return [details.transitions as NonNullable<ItemDetails["transitions"]>[number]];
}
